import { selectAccountNowInventoryLeadTimeInDays } from '@epicuro-next/state/accounts-state';
import {
  basketItemToRemoveBasketUpdateItem,
  BasketUpdateShops,
  selectOperableBasketItems,
} from '@epicuro-next/state/basket-state';
import {
  getUTCNow,
  getUTCNowPlusDays,
  isBefore,
  isSameDate,
  utcDateTime,
} from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';
import { BasketUpdateItem } from 'graphql-api/baskets-api-interfaces';
import { BasketItem, OrderType } from 'graphql-api/gql-api-interfaces';
import { pipe } from 'rxjs';

import { DeliveryPeriodType } from '../../models';
import { requestedDeliveryDateToTimestamp } from '../../shared/requested-delivery-date';
import { getDeliveryShopKey } from '../../shared/shared';
import { isTrueDemandInventory } from '../../shared/true-demand-inventory';
import { updateOrAddInventoryByDate } from '../../shared/update-or-add-inventory-by-date';
import {
  selectDeliveryItemsAndPeriods,
  selectModifiedProducts,
  selectProductSource,
} from '../business/config-strip-business.selectors';
import {
  selectBackOrderEnabledRule,
  selectSkipInventoryCheckEnabled,
} from '../config-strip-configuration.selectors';
import {
  selectPrepackEntities,
  selectProductBrandId,
  selectSizeEntities,
} from '../data/config-strip.data.selectors';
import {
  availableInventoriesForSelectedSizeForOrderType,
  availableInventoriesQuantitiesForDeliveryPeriod,
} from '../shared/inventory-overview';
import { splitQuantities } from '../shared/split-quantities';

import { getPastBasketItems } from './get-past-basket-items-to-remove';

// tslint:disable:parameters-max-number
export const selectBasketUpdateItems = createSelector(
  selectDeliveryItemsAndPeriods,
  selectProductBrandId,
  selectModifiedProducts,
  selectProductSource,
  selectSizeEntities,
  selectPrepackEntities,
  selectOperableBasketItems,
  selectBackOrderEnabledRule,
  selectSkipInventoryCheckEnabled,
  selectAccountNowInventoryLeadTimeInDays,
  (
    { deliveryItems: deliveryItemsState, nowDeliveryPeriods },
    brandId,
    modifiedProducts,
    source,
    sizes,
    prepacks,
    existingBasketItems,
    backOrderEnabled,
    skipInventoryCheckEnabled,
    accountNowInventoryLeadTimeDays,
    // tslint:disable-next-line:no-big-function
  ) => {
    if (!deliveryItemsState || !Object.keys(deliveryItemsState).length) {
      return null;
    }

    let nowStamp = getUTCNow();

    if (!backOrderEnabled && accountNowInventoryLeadTimeDays > 0) {
      // update now requested date to be in the future (Birkenstock BUSA)
      nowStamp = getUTCNowPlusDays(accountNowInventoryLeadTimeDays);
    }

    // tslint:disable-next-line:no-big-function
    return Object.keys(deliveryItemsState).reduce((result, deliveryShop) => {
      const deliveryPeriods =
        nowDeliveryPeriods?.[deliveryShop]?.[DeliveryPeriodType.Now]
          ?.deliveryPeriods;
      let nowDeliveryPeriod =
        deliveryPeriods && deliveryPeriods[0] !== DeliveryPeriodType.Now
          ? deliveryPeriods[0]
          : nowStamp;

      if (isBefore(new Date(nowDeliveryPeriod), new Date(nowStamp))) {
        nowDeliveryPeriod = nowStamp;
      }

      let basketUpdateItems = Object.keys(
        deliveryItemsState[deliveryShop],
      ).flatMap((deliveryPeriod) => {
        const deliveryItems = deliveryItemsState[deliveryShop][deliveryPeriod];

        let returnItems: BasketUpdateItem[] = Object.keys(deliveryItems.items)
          .filter(
            (productId) =>
              modifiedProducts?.[deliveryShop]?.[productId] !== undefined,
          )
          .map((productId) => {
            const deliveryItem = deliveryItems.items[productId];
            return (
              splitQuantities(
                productId,
                deliveryItems,
                deliveryPeriod,
                sizes,
                prepacks,
                backOrderEnabled,
                skipInventoryCheckEnabled,
              )
                .map(
                  ({ requestedDeliveryDate, requestedQuantity, inventoryId }) =>
                    ({
                      brandId,
                      productId,
                      note: deliveryItem.note,
                      requestedQuantity,
                      orderType: deliveryItems.orderType,
                      requestedDeliveryDate: requestedDeliveryDateToTimestamp(
                        requestedDeliveryDate,
                        deliveryItems.deliveryPeriodType,
                        nowDeliveryPeriod,
                      ),
                      originInformation: source?.details ?? {},
                      source: {
                        type: source?.type,
                      },
                      displayDateFrom: deliveryItem.displayDateFrom,
                      consignmentStartDate: deliveryItem.consignmentStartDate,
                      consignmentEndDate: deliveryItem.consignmentEndDate,
                      inventoryId,
                    } as BasketUpdateItem),
                )
                // merge same inventory id and same requested delivery date due to duplicate restriction on BE
                .reduce(updateOrAddInventoryByDate, [])
            );
          })
          .flat();

        returnItems = returnItems
          .map((item) => {
            if (item.orderType !== OrderType.Now) {
              return item;
            }

            if (
              item.requestedDeliveryDate &&
              isBefore(
                new Date(item.requestedDeliveryDate),
                new Date(nowDeliveryPeriod),
              )
            ) {
              item.requestedDeliveryDate = nowDeliveryPeriod;
            }

            return item;
          })
          .reduce((acc: BasketUpdateItem[], item) => {
            if (
              item.orderType !== OrderType.Now ||
              isTrueDemandInventory(item.inventoryId as string)
            ) {
              acc.push(item);

              return acc;
            }

            const existingItem = acc.find(
              (product) =>
                product.requestedDeliveryDate === item.requestedDeliveryDate &&
                product.productId === item.productId,
            );

            if (!existingItem) {
              acc.push(item);

              return acc;
            }

            existingItem.requestedQuantity += item.requestedQuantity;

            return acc;
          }, []);

        return returnItems;
      });

      // find all basket items that does not have inventories anymore and
      // set requestQuantity to 0 so they are removed from basket
      const backOrderBasketItems = existingBasketItems
        // take only backOrders
        .filter(
          (item: BasketItem) =>
            item.orderType === OrderType.Now &&
            utcDateTime(item.requestedDeliveryDate).toISODate() >
              utcDateTime(nowStamp).toISODate(),
        )
        .filter((item) => {
          const productId = item.productId;
          const inventoryItems = sizes[productId]?.inventoryItems;

          if (!inventoryItems) {
            return false;
          }

          const inventoryOverviews = pipe(
            availableInventoriesForSelectedSizeForOrderType,
            availableInventoriesQuantitiesForDeliveryPeriod,
          )(
            inventoryItems.filter(
              (inventoryItem) => inventoryItem.orderType === OrderType.Now,
            ),
          );

          return !inventoryOverviews?.some(
            (inventoryOverview) =>
              inventoryOverview.availableTo >= item.requestedDeliveryDate &&
              inventoryOverview.deliveryPeriod <= item.requestedDeliveryDate &&
              inventoryOverview.maxQuantity >= item.requestedQuantity,
          );
        })
        .map(basketItemToRemoveBasketUpdateItem);

      const deliveryShopExistingBasketItems = existingBasketItems.filter(
        ({ accountId, addressId }) =>
          getDeliveryShopKey({ accountId, addressId: addressId as string }) ===
          deliveryShop,
      );
      const pastItems = getPastBasketItems(
        deliveryShop,
        basketUpdateItems,
        deliveryShopExistingBasketItems,
        nowDeliveryPeriod,
        backOrderEnabled,
      );

      // we need to update items basketItemId from the past since CS will create duplicates ordered for NOW date
      basketUpdateItems = basketUpdateItems.map((currentBasketItem) => {
        if (currentBasketItem.orderType !== OrderType.Now) {
          return currentBasketItem;
        }

        const pastItem = pastItems.find(
          (item) => item.productId === currentBasketItem.productId,
        );

        if (!pastItem || pastItem.orderType !== OrderType.Now) {
          return currentBasketItem;
        }

        return {
          ...currentBasketItem,
          basketItemId: pastItem.basketItemId,
        };
      });

      const removeItems = backOrderEnabled
        ? []
        : deliveryShopExistingBasketItems
            .filter((item) => {
              if (item.orderType !== OrderType.Now) {
                return false;
              }

              const isInUpdate = basketUpdateItems.find(
                (updateItem) =>
                  updateItem.productId === item.productId &&
                  isSameDate(
                    item.requestedDeliveryDate,
                    updateItem.requestedDeliveryDate,
                  ) &&
                  updateItem.requestedQuantity !== item.requestedQuantity,
              );

              return isInUpdate;
            })
            .map(basketItemToRemoveBasketUpdateItem);

      return {
        ...result,
        [deliveryShop]: [
          ...backOrderBasketItems,
          ...basketUpdateItems,
          ...removeItems,
        ],
      };
    }, {} as BasketUpdateShops);
  },
);
